import React, { useState, useEffect, useRef } from 'react';
import { Button, FormControl, ControlLabel, FormGroup, Alert, Modal } from 'ui';
import API from 'apis/API';
import { useQueryClient } from '@tanstack/react-query';
import HelpLink from 'projects/components/collab/HelpLink';

const MfaModal = ({ show, onHide, mfaEnabled }) => {
  const [step, setStep] = useState('verify'); // 'verify', 'disable', 'success-enabled', 'success-disabled'
  const [provisioningUri, setProvisioningUri] = useState('');
  const [secret, setSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [disableMfaCode, setDisableMfaCode] = useState('');
  const [error, setError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const otpInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const disableMfaCodeRef = useRef(null);
  const queryClient = useQueryClient();

  // Reset state and setup MFA when modal opens
  // Only depend on `show` - we capture mfaEnabled at open time
  useEffect(() => {
    if (show) {
      setOtpCode('');
      setPassword('');
      setDisableMfaCode('');
      setError('');
      setOtpError('');
      setPasswordError('');
      setProvisioningUri('');
      setSecret('');

      if (mfaEnabled) {
        setStep('disable');
      } else {
        // Immediately call setup when opening enable flow
        setStep('verify');
        setLoading(true);
        API.post('/api/mfa/setup')
          .then((response) => {
            setProvisioningUri(response.data.provisioning_uri);
            setSecret(response.data.secret);
          })
          .catch((err) => {
            setError(err.response?.data?.error || 'Failed to setup MFA');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Focus on password input when QR code loads
  useEffect(() => {
    if (provisioningUri && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [provisioningUri]);

  // Focus on MFA code input for disable flow
  useEffect(() => {
    if (step === 'disable' && disableMfaCodeRef.current) {
      disableMfaCodeRef.current.focus();
    }
  }, [step]);

  const handleEnableSubmit = (e) => {
    e.preventDefault();
    handleEnable();
  };

  const handleDisableSubmit = (e) => {
    e.preventDefault();
    handleDisable();
  };

  const handleEnable = async () => {
    // Clear previous errors
    setOtpError('');
    setPasswordError('');
    setError('');

    // Validate fields
    let hasError = false;
    if (!otpCode) {
      setOtpError('Please enter the verification code');
      hasError = true;
    }
    if (!password) {
      setPasswordError('Please enter your password');
      hasError = true;
    }
    if (hasError) return;

    setLoading(true);
    try {
      await API.post('/api/mfa/enable', { otp_code: otpCode, password: password });
      queryClient.invalidateQueries({
        queryKey: ['users', 'current']
      });
      setStep('success-enabled');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Invalid verification code';
      // Route error to appropriate field based on message
      if (errorMsg.toLowerCase().includes('password')) {
        setPasswordError(errorMsg);
      } else {
        setOtpError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disableMfaCode) {
      setError('Please enter your verification code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await API.delete('/api/mfa/disable', { mfa_code: disableMfaCode });
      queryClient.invalidateQueries({
        queryKey: ['users', 'current']
      });
      setStep('success-disabled');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOtpCode('');
    setPassword('');
    setDisableMfaCode('');
    setError('');
    setOtpError('');
    setPasswordError('');
    onHide();
  };

  // Format secret for display (groups of 4)
  const formatSecret = (secret) => {
    if (!secret) return '';
    return secret.match(/.{1,4}/g)?.join('-') || secret;
  };

  const renderEnableFlow = () => {
    return (
        <form onSubmit={handleEnableSubmit}>
          <Modal.Body style={{ padding: '20px 60px 50px' }}>
            <h4 style={{ fontSize: '18px', marginTop: '10px', marginBottom: '20px' }}>{getModalTitle()}</h4>
            {error && (
              <Alert variant="danger" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                {error}
              </Alert>
            )}

            <ol style={{ paddingLeft: '0', marginLeft: '0', listStylePosition: 'inside' }}>
              <li>Open the Google Authenticator app</li>
              <li style={{ marginTop: '10px' }}>
                Scan this QR code
                <div style={{ margin: '15px 0', width: '200px', height: '200px', border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {provisioningUri ? (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(provisioningUri)}`}
                      alt="QR Code"
                      style={{ padding: '10px' }}
                    />
                  ) : (
                    <span style={{ color: '#999' }}>QR code loading...</span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                  Or enter this code manually: {formatSecret(secret)}
                </div>
              </li>
              <li style={{ marginTop: '15px' }}>
                Enter your account password
                <div style={{ maxWidth: '320px' }}>
                  {passwordError && (
                    <Alert variant="warning" style={{ marginTop: '10px', marginBottom: '0', paddingTop: '8px', paddingBottom: '8px' }}>{passwordError}</Alert>
                  )}
                  <FormGroup controlId="password" style={{ marginTop: '10px' }}>
                    <FormControl
                      type="password"
                      placeholder="Enter Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      ref={ref => { passwordInputRef.current = ref; }}
                    />
                  </FormGroup>
                </div>
              </li>
              <li style={{ marginTop: '15px' }}>
                Enter the 6 digit code you see in the Google Authenticator app
                <div style={{ maxWidth: '320px' }}>
                  {otpError && (
                    <Alert variant="warning" style={{ marginTop: '10px', marginBottom: '0', paddingTop: '8px', paddingBottom: '8px' }}>{otpError}</Alert>
                  )}
                  <FormGroup controlId="otpCode" style={{ marginTop: '10px' }}>
                    <FormControl
                      type="text"
                      placeholder="Enter 6 digit verification code"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      maxLength={6}
                      autoComplete="one-time-code"
                      ref={ref => { otpInputRef.current = ref; }}
                    />
                  </FormGroup>
                </div>
              </li>
            </ol>
            <div style={{ marginTop: '20px' }}>
              <Button variant="blue" type="submit" disabled={loading}>
                {loading ? 'Enabling...' : 'Turn on two factor authentication'}
              </Button>
            </div>
            {process.env['INTERCOM_ARTICLE_MFA'] && (
              <div style={{ marginTop: '15px' }}>
                <HelpLink intercomArticle={process.env['INTERCOM_ARTICLE_MFA']}>
                  Learn about two factor authentication
                </HelpLink>
              </div>
            )}
          </Modal.Body>
        </form>
      );
  };

  const renderDisableFlow = () => {
    return (
      <form onSubmit={handleDisableSubmit}>
        <Modal.Body style={{ padding: '20px 60px 50px' }}>
          <h4 style={{ fontSize: '18px', marginTop: '10px', marginBottom: '20px' }}>{getModalTitle()}</h4>

          <p>Enter the 6 digit code you see in the Google Authenticator app</p>

          <div style={{ maxWidth: '320px' }}>
            {error && (
              <Alert variant="warning" style={{ marginTop: '10px', marginBottom: '0', paddingTop: '8px', paddingBottom: '8px' }}>{error}</Alert>
            )}
            <FormGroup controlId="disableMfaCode" style={{ marginTop: '10px' }}>
              <FormControl
                type="text"
                placeholder="Enter 6 digit verification code"
                value={disableMfaCode}
                onChange={(e) => setDisableMfaCode(e.target.value)}
                maxLength={6}
                autoComplete="one-time-code"
                ref={ref => { disableMfaCodeRef.current = ref; }}
              />
            </FormGroup>
          </div>
          <div style={{ marginTop: '20px' }}>
            <Button variant="blue" type="submit" disabled={loading}>
              {loading ? 'Turning off...' : 'Turn off two factor authentication'}
            </Button>
          </div>
          {process.env['INTERCOM_ARTICLE_MFA'] && (
            <div style={{ marginTop: '15px' }}>
              <HelpLink intercomArticle={process.env['INTERCOM_ARTICLE_MFA']}>
                Learn about two factor authentication
              </HelpLink>
            </div>
          )}
        </Modal.Body>
      </form>
    );
  };

  const renderSuccessEnabled = () => {
    return (
      <Modal.Body style={{ padding: '20px 60px 50px' }}>
        <h4 style={{ fontSize: '18px', marginTop: '10px', marginBottom: '20px' }}>Success</h4>
        <Alert variant="success">
          Two factor authentication was turned on
        </Alert>
        <p>You can turn off two-factor authentication in your account settings.</p>
        <div style={{ marginTop: '20px' }}>
          <Button variant="blue" onClick={handleClose}>Done</Button>
        </div>
      </Modal.Body>
    );
  };

  const renderSuccessDisabled = () => {
    return (
      <Modal.Body style={{ padding: '20px 60px 50px' }}>
        <h4 style={{ fontSize: '18px', marginTop: '10px', marginBottom: '20px' }}>Success</h4>
        <Alert variant="success">
          Two factor authentication was turned off
        </Alert>
        <p>You can turn on two-factor authentication in your account settings.</p>
        <div style={{ marginTop: '20px' }}>
          <Button variant="blue" onClick={handleClose}>Done</Button>
        </div>
      </Modal.Body>
    );
  };

  const getModalTitle = () => {
    if (step === 'success-enabled') return 'Two Factor Authentication Enabled';
    if (step === 'success-disabled') return 'Two Factor Authentication Disabled';
    return mfaEnabled ? 'Turn Off Two Factor Authentication' : 'Turn On Two Factor Authentication';
  };

  const renderContent = () => {
    if (step === 'success-enabled') return renderSuccessEnabled();
    if (step === 'success-disabled') return renderSuccessDisabled();
    if (step === 'disable') return renderDisableFlow();
    return renderEnableFlow();
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: '0', paddingTop: '15px', paddingRight: '20px' }} />
      {renderContent()}
    </Modal>
  );
};

export default MfaModal;
